import { Card, CardContent } from '@ui/card.tsx';
import { UserPlus } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useUser } from '@r/api/auth.tsx';
import { getActiveWorkspaceUniversal } from '@r/helpers/api.ts';
import { HierarchyList } from '@admin/components/hierarchy/hierarchy-list.tsx';
import { CreateHierarchyDialog } from '@admin/components/hierarchy/create-hierarchy-dialog.tsx';
import { invalidateSalesHierarchiesQueries } from '@admin/components/hierarchy/hierarchy-utils.ts';

export const Hierarchy = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useUser();
  const activeWorkspace = getActiveWorkspaceUniversal(user.data?.visibleWorkspaces ?? []);
  // Le POST /admin/sales-hierarchies requiert ROLE_ADMIN côté back (le GET
  // accepte ROLE_SALES_ADMIN) : on masque les actions d'écriture (créer,
  // supprimer) aux users qui n'ont que ROLE_SALES_ADMIN pour éviter un 403.
  const canManageHierarchies =
    activeWorkspace?.roles?.includes('ROLE_ADMIN') ||
    activeWorkspace?.roles?.includes('ROLE_FIDDY_ADMIN');

  const handleCreated = (hierarchy: { id: number }) => {
    invalidateSalesHierarchiesQueries(queryClient);
    navigate({ to: '/settings/hierarchy/$id', params: { id: String(hierarchy.id) } });
  };

  return (
    <div className="flex flex-col space-y-10 grow">
      <h1 className="text-4xl font-bold">{t('menu.admin.hierarchy')}</h1>

      {canManageHierarchies && (
        <Card className="w-full">
          <CardContent className="flex justify-between items-center gap-6 p-6">
            <div className="space-y-1">
              <h2 className="flex gap-2 items-center text-xl font-bold">
                <UserPlus className="w-6 h-6" />
                {t('admin.hierarchy.create.title')}
              </h2>
              <p className="text-muted-foreground">{t('admin.hierarchy.create.description')}</p>
            </div>
            <CreateHierarchyDialog onCreated={handleCreated} />
          </CardContent>
        </Card>
      )}

      <HierarchyList canManage={Boolean(canManageHierarchies)} />
    </div>
  );
};
